/**
 * Integration API Client.
 */

import { apiClient } from "@/lib/api/client";
import type { CredentialFieldSpec } from "@speakai/shared";
import type { AutoJoinSettings, CalendarAccount, CalendarType } from "@speakai/common/src/calendar/api";
import {
  APP_URL,
  GOOGLE_CLIENT_ID,
  GOOGLE_DRIVE_CLIENT_ID,
  MICROSOFT_CLIENT_ID,
  SLACK_CLIENT_ID,
  SLACK_SCOPES,
  VIMEO_CLIENT_ID,
  VIMEO_SCOPES,
} from "@/config/constants";

// -- OAuth URL Constants --------------------------------------------

const getOrigin = () =>
  typeof window !== "undefined" && window.location?.origin
    ? window.location.origin
    : APP_URL;

export const getGoogleCalendarRedirectUrl = () =>
  `${getOrigin()}/integrations/calendar/google`;
export const getOutlookCalendarRedirectUrl = () =>
  `${getOrigin()}/integrations/calendar/outlook`;

const GOOGLE_DRIVE_REDIRECT_URL = `${APP_URL}/integrations/googledrive`;
const SLACK_REDIRECT_URL = `${APP_URL}/integrations/slack`;
const SLACK_AUTHORIZE_URL = "https://slack.com/oauth/v2/authorize";
const VIMEO_REDIRECT_URL = `${APP_URL}/integrations/vimeo`;
const VIMEO_AUTHORIZE_URL = "https://api.vimeo.com/oauth/authorize";

// -- Types ----------------------------------------------------------

export interface IntegrationAccount {
  isActive: boolean;
  isDeleted?: boolean;
}

export interface IntegrationAccounts {
  [key: string]: IntegrationAccount;
}

export interface SlackIntegration {
  accountId: string;
  teamName: string;
  isActive: boolean;
  events?: SlackEvent[];
}

export interface SlackEvent {
  type: string;
  channelId: string;
  isActive: boolean;
}

export interface SlackChannel {
  id: string;
  name: string;
  isPrivate: boolean;
}

export interface VimeoIntegration {
  accountId: string;
  name: string;
  email: string;
  isActive: boolean;
}

export interface VimeoVideo {
  uri: string;
  name: string;
  duration: number;
  created_time: string;
}

export interface VimeoFolder {
  folderId: string;
  name: string;
  totalVideo: number;
  created_time: string;
}

export interface VimeoAlbum {
  albumId: string;
  name: string;
  totalVideo: number;
  created_time: string;
}

export interface VimeoPagination {
  total: number;
  page: number;
  perPage: number;
}

export interface GoogleDriveIntegration {
  accountId: string;
  email: string;
  accountName: string;
  isActive: boolean;
  tokenExpiresIn?: number;
}

// Names mirror @speakai/shared ICalendarEvent; wire types are strings. Required on
// purpose: optional is what let a casing mismatch compile to silent undefined.
export interface CalendarEvent {
  eventId: string;
  title: string;
  startTime: string;
  endTime: string;
  meetingStatus: string;
  platform: string;
  meetingURL: string;
}

export interface CalendarEventsFilter {
  sortBy: string;
  sortDir: string;
  query?: string;
  meetingStatus?: string;
}

// -- OAuth URL Generators -------------------------------------------

/**
 * Google Calendar OAuth URL.
 * Requests calendar.readonly scope in addition to profile/email.
 * access_type=offline + prompt=consent ensures a refresh token is returned.
 */
export function getGoogleCalendarAuthUrl(nonce?: string): string {
  const scopes = [
    "https://www.googleapis.com/auth/userinfo.profile",
    "https://www.googleapis.com/auth/userinfo.email",
    "https://www.googleapis.com/auth/calendar.readonly",
  ];
  const state = JSON.stringify({ ssoType: "google", ssoAction: "connect", ...(nonce ? { nonce } : {}) });
  const params = new URLSearchParams({
    client_id: GOOGLE_CLIENT_ID,
    redirect_uri: getGoogleCalendarRedirectUrl(),
    response_type: "code",
    scope: scopes.join(" "),
    access_type: "offline",
    prompt: "consent",
    include_granted_scopes: "true",
    state,
  });
  return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
}

/**
 * Microsoft Outlook Calendar OAuth URL.
 * Requests Calendars.Read scope in addition to the base User.Read scopes.
 */
export function getOutlookCalendarAuthUrl(nonce?: string): string {
  const scopes = [
    "offline_access",
    "openid",
    "email",
    "https://graph.microsoft.com/User.Read",
    "https://graph.microsoft.com/Calendars.Read",
  ];
  const state = JSON.stringify({ ssoType: "microsoft", ssoAction: "connect", ...(nonce ? { nonce } : {}) });
  const params = new URLSearchParams({
    client_id: MICROSOFT_CLIENT_ID,
    redirect_uri: getOutlookCalendarRedirectUrl(),
    response_type: "code",
    scope: scopes.join(" "),
    response_mode: "query",
    prompt: "select_account",
    state,
  });
  return `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?${params.toString()}`;
}

export function getGoogleDriveAuthUrl(): string {
  const scopes = [
    "https://www.googleapis.com/auth/userinfo.profile",
    "https://www.googleapis.com/auth/userinfo.email",
    "https://www.googleapis.com/auth/drive.readonly",
  ];
  const params = new URLSearchParams({
    client_id: GOOGLE_DRIVE_CLIENT_ID,
    redirect_uri: GOOGLE_DRIVE_REDIRECT_URL,
    response_type: "code",
    scope: scopes.join(" "),
    access_type: "offline",
    prompt: "consent",
    include_granted_scopes: "true",
    state: "google_drive",
  });
  return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
}

export function getSlackAuthUrl(): string {
  const scopes = SLACK_SCOPES.split(",").map((s) => s.trim());
  const params = new URLSearchParams({
    client_id: SLACK_CLIENT_ID,
    redirect_uri: SLACK_REDIRECT_URL,
    scope: scopes.join(","),
    state: "slack",
  });
  return `${SLACK_AUTHORIZE_URL}?${params.toString()}`;
}

/**
 * Vimeo OAuth URL.
 * Caller should generate a per-request nonce, persist it in
 * sessionStorage, and verify it on the callback to defeat CSRF.
 */
export function getVimeoAuthUrl(nonce?: string): string {
  const params = new URLSearchParams({
    response_type: "code",
    client_id: VIMEO_CLIENT_ID,
    scope: VIMEO_SCOPES,
    redirect_uri: VIMEO_REDIRECT_URL,
  });
  if (nonce) {
    params.set("state", JSON.stringify({ ssoType: "vimeo", nonce }));
  }
  return `${VIMEO_AUTHORIZE_URL}?${params.toString()}`;
}

// -- API Functions --------------------------------------------------

/** Get all connected integration accounts (hub page summary). */
export function getIntegrations() {
  return apiClient.get<IntegrationAccounts>("/integration/accounts");
}

// -- AI Actions Catalog (Composio) ----------------------------------

export type IntegrationCatalogStatus =
  | "connected"
  | "not_connected"
  | "expired"
  | "pending";

/** How a provider is authenticated: OAuth redirect vs. pasted API key/token. */
export type IntegrationAuthTypeValue = "oauth" | "api_key";

export interface IntegrationCatalogEntry {
  provider: string;
  name: string;
  icon: string;
  status: IntegrationCatalogStatus;
  capabilities?: string;
  category?: string;
  /**
   * Auth strategy for this provider. `oauth` (or undefined, for back-compat
   * with older catalog payloads) uses the redirect flow; `api_key` renders a
   * key-entry dialog populated from `fields`.
   */
  authType?: IntegrationAuthTypeValue;
  /** Credential fields to render for an `api_key` provider. */
  fields?: CredentialFieldSpec[];
  /**
   * Email address of the connected account, returned by the server when
   * the provider is in a connected or expired state. Shown inline in the
   * integration row next to the capabilities description.
   */
  accountEmail?: string;
  /**
   * Present when the account authenticated fine but its automation triggers could
   * not be subscribed (e.g. the third-party plan lacks a required licence). The
   * connection still works for chat tools; it just cannot power an automation.
   */
  automationUnavailable?: { reason: string; message: string };
  /** Starter automations available for this provider; absent when it has none. */
  templateCount?: number;
}

export function getIntegrationsCatalog() {
  return apiClient.get<IntegrationCatalogEntry[]>("/integrations/catalog");
}

export function connectIntegration(provider: string) {
  return apiClient.post<{ connectUrl: string; alreadyConnected?: boolean }>(
    `/integrations/connect/${encodeURIComponent(provider)}`
  );
}

export function disconnectIntegration(provider: string) {
  return apiClient.post<{ ok: true }>(
    `/integrations/disconnect/${encodeURIComponent(provider)}`
  );
}

/** Discriminator for a key-connect/update verification failure (422). */
export type KeyConnectErrorCode = "verify" | "unavailable";

/**
 * Connect an `api_key` provider by submitting credential fields. The server
 * probes the credentials before persisting; on a probe failure it returns 422
 * with an `errorCode` of `verify` (bad key) or `unavailable` (no probe wired).
 */
export function connectWithKey(
  provider: string,
  fields: Record<string, string>
) {
  return apiClient.post<{ status: "connected" }>(
    `/integrations/connect-key/${encodeURIComponent(provider)}`,
    { fields }
  );
}

/**
 * Rotate the key for an already-connected `api_key` provider. Same body and
 * error contract as {@link connectWithKey}; the existing connection is left
 * untouched if the new key fails verification.
 */
export function updateKey(provider: string, fields: Record<string, string>) {
  return apiClient.post<{ status: "connected" }>(
    `/integrations/update-key/${encodeURIComponent(provider)}`,
    { fields }
  );
}

// -- Calendar -------------------------------------------------------

/**
 * Whether calendar integrations are disabled by the company admin.
 * Server: GET /v1/integration/calendar/disabled
 */
export function isCalendarDisabled() {
  return apiClient.get<{ showBanner: boolean; calendarType?: string }>(
    "/integration/calendar/disabled"
  );
}

export function getAllCalendarAccounts() {
  return apiClient.get<Array<{ calendars: CalendarAccount }>>(
    "/integration/calendar"
  );
}

export function getCalendarAccountDetails(type: CalendarType) {
  return apiClient.get<Array<{ calendars: CalendarAccount }>>(
    `/integration/calendar/${type}`
  );
}

export function connectCalendar(
  type: CalendarType,
  data: { code: string; redirectUrl?: string },
) {
  return apiClient.post(`/integration/calendar/${type}/connect`, data);
}

export function disconnectCalendar(type: CalendarType, calendarId: string) {
  return apiClient.get(`/integration/calendar/${type}/disconnect/${calendarId}`);
}

export function updateAutoJoinMeetings(
  type: CalendarType,
  calendarId: string,
  data: AutoJoinSettings
) {
  return apiClient.post(`/integration/calendar/${type}/auto-join`, {
    calendarId,
    autoJoin: data,
  });
}

export function getCalendarEvents(
  type: CalendarType,
  pageIndex: number,
  pageSize: number,
  eventsFilter: CalendarEventsFilter
) {
  let url = `/integration/calendar/${type}/events?page=${pageIndex}&pageSize=${pageSize}&query=${eventsFilter.query || ""}&sortBy=${eventsFilter.sortBy}:${eventsFilter.sortDir}`;
  if (eventsFilter.meetingStatus) {
    url += `&meetingStatus=${eventsFilter.meetingStatus}`;
  }
  return apiClient.get<{ events: CalendarEvent[]; totalCount: number }>(url);
}

export function getUniqueCalendarEvents() {
  return apiClient.get("/integration/calendar/events/unique");
}

// -- Slack ----------------------------------------------------------

export function getSlackAccounts() {
  return apiClient.get<SlackIntegration[]>("/integration/slack");
}

export function connectSlack(data: { code: string }) {
  return apiClient.post("/integration/slack/connect", data);
}

export function disconnectSlack(data: { accountId: string }) {
  return apiClient.post("/integration/slack/disconnect", data);
}

export function getSlackChannels(accountId: string) {
  return apiClient.get<SlackChannel[]>(
    `/integration/slack/${accountId}/channels`
  );
}

export function updateSlackEvents(
  accountId: string,
  data: { events: SlackEvent[] }
) {
  return apiClient.put(`/integration/slack/${accountId}/events`, data);
}

export function sendTranscriptionToSlack(data: {
  mediaId: string;
  channelId: string;
  channelName: string;
  accountId: string;
}) {
  return apiClient.post("/integration/slack/send-transcription", data);
}

// -- Vimeo ----------------------------------------------------------

export function getVimeoDetails() {
  return apiClient.get<VimeoIntegration[]>("/integration/vimeo");
}

export function connectVimeo(data: { code: string }) {
  return apiClient.post("/integration/vimeo/connect", data);
}

export function disconnectVimeo(data: { accountId: string }) {
  return apiClient.post("/integration/vimeo/disconnect", data);
}

export function getVimeoVideos(page: number, pageSize: number) {
  return apiClient.get<{
    videos: VimeoVideo[];
    pagination: VimeoPagination;
  }>(`/integration/vimeo/videos?page=${page}&pageSize=${pageSize}`);
}

export function getVimeoFolders(page: number, pageSize: number) {
  return apiClient.get<{
    folders: VimeoFolder[];
    pagination: VimeoPagination;
  }>(`/integration/vimeo/folders?page=${page}&pageSize=${pageSize}`);
}

export function getVimeoAlbums(page: number, pageSize: number) {
  return apiClient.get<{
    albums: VimeoAlbum[];
    pagination: VimeoPagination;
  }>(`/integration/vimeo/albums?page=${page}&pageSize=${pageSize}`);
}

export function getVimeoFolderVideos(
  folderId: string,
  page: number,
  pageSize: number
) {
  return apiClient.get<{
    videos: VimeoVideo[];
    pagination: VimeoPagination;
  }>(
    `/integration/vimeo/folder/${folderId}?page=${page}&pageSize=${pageSize}`
  );
}

export function getVimeoAlbumVideos(
  albumId: string,
  page: number,
  pageSize: number
) {
  return apiClient.get<{
    videos: VimeoVideo[];
    pagination: VimeoPagination;
  }>(
    `/integration/vimeo/album/${albumId}?page=${page}&pageSize=${pageSize}`
  );
}

export interface VimeoDownloadInfo {
  createdAt: string;
  description: string;
  downloadUrl: string;
  duration: number;
  title: string;
  vimeoId: string;
  size?: number;
}

/**
 * Fetch Vimeo download metadata for a URL. Matches Angular `getVimeoDownloadInfo`.
 * Server: GET /v1/integration/vimeo/download?url=<vimeoUrl>
 */
export function getVimeoDownloadInfo(url: string) {
  return apiClient.get<VimeoDownloadInfo[] | VimeoDownloadInfo | null>(
    `/integration/vimeo/download?url=${encodeURIComponent(url)}`
  );
}

/**
 * Fetch single Vimeo video metadata by ID. Matches Angular `getVideoInfo`.
 * Server: GET /v1/integration/vimeo/video/:videoId
 */
export function getVimeoVideoInfo(videoId: string) {
  return apiClient.get<VimeoVideo>(
    `/integration/vimeo/video/${encodeURIComponent(videoId)}`
  );
}

/**
 * Import a Vimeo video by URL. The server has no POST /vimeo/import route — both
 * Angular and React resolve "import" via the GET /vimeo/download metadata fetch,
 * after which the caller hands the result to /media/upload (syncRecordedFile).
 *
 * Normalizes the server response (array or single) to a single VimeoDownloadInfo,
 * matching the original shape this helper returned.
 */
export async function importVimeoUrl(url: string) {
  const res = await getVimeoDownloadInfo(url);
  if (!res.success) return res as { success: false; error?: string };
  const payload = res.data;
  const single: VimeoDownloadInfo | null = Array.isArray(payload)
    ? (payload[0] ?? null)
    : (payload ?? null);
  return { ...res, data: single } as {
    success: true;
    data: VimeoDownloadInfo | null;
  };
}

// -- Google Drive ---------------------------------------------------

export function getGoogleDriveAccounts() {
  return apiClient.get<GoogleDriveIntegration[]>("/integration/googledrive");
}

export function connectGoogleDrive(data: { code: string }) {
  return apiClient.post("/integration/googledrive/connect", data);
}

export function disconnectGoogleDrive(data: { accountId: string }) {
  return apiClient.post("/integration/googledrive/disconnect", data);
}

export function uploadDriveLink(data: {
  url: string;
  sourceLanguage?: string;
  folderId?: string;
}) {
  return apiClient.post("/integration/googledrive/upload", data);
}

// -- Sync (shared by Zoom/Vimeo import) -----------------------------

export function syncRecordedFile(data: {
  mediaType: string;
  name: string;
  uploadType: string;
  uploadReferenceId: string;
  url: string;
  description?: string;
}) {
  return apiClient.post("/media/upload", data);
}
